import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { Select } from "antd";
import { ReactComponent as DownArrow } from "../../Assets/Icons/greyDownArrow.svg";

const AveragePurchaseFrequency = () => {
  const { redemptionFrequencies } = useSelector(
    (state) => state?.merchantReducer
  );
  const [selectedFilter, setSelectedFilter] = useState("Last Year");
  const [filteredSeries, setFilteredSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [maxDataValue, setMaxDataValue] = useState(10);
  const [step, setStep] = useState(2);
  const [loading, setLoading] = useState(true);
  const [chartOptions, setChartOptions] = useState({
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
    },
    colors: ["#1E90FF", "#FF6347", "#32CD32"],
    chart: {
      fontFamily: "Poppins, sans-serif",
      height: 335,
      type: "bar",
      toolbar: { show: false },
    },
    dataLabels: { enabled: false },
    plotOptions: {
      bar: {
        columnWidth: `${Math.min(100 / (categories.length + 1), 50)}%`,
      },
    },
    xaxis: {
      type: "category",
      categories: [],
      title: {
        text: `Time Period (${selectedFilter})`,
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxDataValue / step) * step,
      tickAmount: Math.ceil(maxDataValue / step),
      labels: {
        formatter: (value) => value,
      },
      title: {
        text: "Redeemed Frequency",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    tooltip: { shared: true, intersect: false },
  });

  const formatDate = (dateString) => {
    // Check if the dateString has a day (i.e., in "YYYY-MM-DD" format)
    if (dateString.includes("-") && dateString.split("-").length === 3) {
      // Full date (e.g., "2025-02-16")
      const date = new Date(dateString);
      const month = date
        .toLocaleString("default", { month: "short" })
        .toUpperCase(); // Abbreviated month (e.g. "FEB")
      const year = date.getFullYear();
      const day = date.getDate();
      return `${day} ${month}`; // "16 FEB"
    } else {
      // Year-Month (e.g., "2025-02")
      const [year, month] = dateString.split("-");
      const monthAbbr = new Date(`${year}-${month}-01`)
        .toLocaleString("default", { month: "short" })
        .toUpperCase();
      return `${monthAbbr} ${year}`; // "FEB 2025"
    }
  };

  const processFrequencyData = (frequencyData) => {
    if (!frequencyData || !frequencyData.success || !frequencyData.data?.data) {
      setFilteredSeries([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    const { data } = frequencyData.data;

    if (!data || data.length === 0) {
      setFilteredSeries([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    // Extract unique periods from the response
    const uniquePeriods = new Set();
    data.forEach((item) => {
      uniquePeriods.add(item.period); // Add each period to the Set to ensure uniqueness
    });

    let sortedPeriods = Array.from(uniquePeriods).sort(); // Sort periods in ascending order
    
    // Take only the most recent 6 periods
    sortedPeriods = sortedPeriods.slice(-6);
    
    // Extract redeemed frequencies for the limited periods
    const frequencies = sortedPeriods.map((period) => {
      const found = data.find((d) => d.period === period);
      return found ? parseFloat(found.redeemedFrequency) : 0;
    });

    // Find max value
    const newMax = Math.max(...frequencies, 5); // Ensure at least 5
    const newStep = Math.ceil(newMax / 5); // Adjust step dynamically

    setMaxDataValue(newMax);
    setStep(newStep);
    // Map data into series by category
    const series = [
      {
        name: "Redeemed Frequency",
        data: frequencies,
      },
    ];

    // Set categories with formatted dates
    const formattedCategories = sortedPeriods.map(formatDate);

    setCategories(formattedCategories);
    setFilteredSeries(series);

    // Update chart options with new categories
    setChartOptions((prev) => ({
      ...prev,
      xaxis: { ...prev.xaxis, categories: formattedCategories },
      yaxis: {
        ...prev.yaxis,
        max: Math.ceil(newMax / newStep) * newStep,
        tickAmount: Math.ceil(newMax / newStep),
      },
    }));

    setLoading(false);
  };

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
    // Note: The actual API call should be handled by the parent component
    // This component now only processes the data from Redux state

    setChartOptions((prev) => ({
      ...prev,
      xaxis: {
        ...prev.xaxis,
        title: {
          text: `Time Period (${filterValue})`,
          style: {
            fontSize: "14px",
            fontWeight: "400",
            color: "#373d3f",
            fontFamily: "Poppins, sans-serif",
          },
        },
      },
    }));
  };

  useEffect(() => {
    if (redemptionFrequencies) {
      processFrequencyData(redemptionFrequencies);
    }
  }, [redemptionFrequencies]);

  useEffect(() => {
    setSelectedFilter("Last Year");
  }, []);

  const isLastWeek = selectedFilter === "Last Week";

  return (
    <div className="rounded-sm border border-stroke bg-white px-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 summary offersChart">
      <div className="flex flex-col gap-4 xl:flex-row xl:gap-0 justify-between w-full mt-4">
        <span className="text-[20px] font-poppins font-[600] text-[black] whitespace-nowrap">
          Average Purchase Frequency
        </span>
        <Select
          value={selectedFilter}
          className="border border-[#00ADEF] rounded-md pr-3 text-lg font-medium outline-none h-[40px]"
          suffixIcon={<DownArrow />}
          options={[
            { label: "Last Week", value: "Last Week" },
            { label: "Last Month", value: "Last Month" },
            { label: "Last Year", value: "Last Year" },
          ]}
          defaultValue={selectedFilter}
          onChange={handleFilterChange}
          dropdownMatchSelectWidth={false}
          style={{ minWidth: "100px", maxWidth: "200px" }}
        />
      </div>
      <div
        className={`w-full flex items-center ${
          filteredSeries.length > 0 && filteredSeries[0]?.data?.length > 0 && !loading
            ? isLastWeek ? "justify-center" : "overflow-x-auto justify-start"
            : "justify-center"
        }`}
      >
        <div id="chartTwo" className={`mt-4 ${isLastWeek ? "w-full" : "min-w-[800px] w-full"} h-[430px]`}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500 text-lg font-medium">
                Loading...
              </div>
            </div>
          ) : filteredSeries[0]?.data.length > 0 &&
            filteredSeries[0]?.data?.length > 0 ? (
            <ReactApexChart
              options={chartOptions}
              series={filteredSeries}
              type="bar"
              height={410}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500 text-lg font-medium">
                No Purchase Frequency Data Found
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AveragePurchaseFrequency;
